import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import {
  commitFaqImport,
  downloadFaqTemplate,
  faqErrorCode,
  faqErrorMessage,
  previewFaqImport,
} from '../../api/faq'
import FaqDownloadButton from './FaqDownloadButton'
import { TONES, fieldLabel, reportMessage, toneOf } from './faqReport'
import useDialogKeys from './useDialogKeys'

const MAX_FILE_BYTES = 2 * 1024 * 1024
const button = 'h-10 rounded-lg px-4 text-sm font-semibold disabled:opacity-50'

function ReportItem({ item }) {
  const { t } = useTranslation()
  return (
    <li className={`rounded-lg border p-2 text-sm ${TONES[toneOf(item.code)]}`}>
      <p className="text-slate-800">
        <span className="font-semibold">
          {item.row ? `${t('faq.import.columns.row')} ${item.row}` : t('faq.import.wholeFile')}
        </span>
        {item.field && <span className="text-slate-500"> · {fieldLabel(t, item.field)}</span>}
        <span>: {reportMessage(t, item)}</span>
      </p>
      {item.changed_fields?.length > 0 && (
        <dl className="mt-1 flex flex-col gap-1 text-xs">
          {item.changed_fields.map((change) => (
            <div key={change.field} className="break-words">
              <dt className="font-medium text-slate-600">{fieldLabel(t, change.field)}</dt>
              <dd className="text-slate-500">{t('faq.import.before')}: {change.before || '—'}</dd>
              <dd className="text-slate-800">{t('faq.import.after')}: {change.after || '—'}</dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  )
}

// Excel import in two steps: the file is checked (preview, nothing written),
// then applied by the preview's token without sending the file again.
export default function FaqImportDialog({ tenantId, showTemplate, onImported, onClose, onPermissionDenied }) {
  const { t, i18n } = useTranslation()
  const [step, setStep] = useState('choose') // choose | preview | done
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)
  const [overwriteStale, setOverwriteStale] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const root = useRef(null)
  // A running commit is not abandoned by a stray Escape.
  const onKeyDown = useDialogKeys(root, () => !busy && onClose())
  // The button that had focus is disabled while busy, or gone with the step:
  // focus returns to the dialog, so Escape and Tab keep working there.
  useEffect(() => {
    if (!busy && !root.current?.contains(document.activeElement)) root.current?.focus()
  }, [busy, step])

  const number = (value) => Number(value).toLocaleString(i18n.language)
  const counted = (value) => ({ count: value, formatted: number(value) })

  const fail = (error) => {
    setMessage(faqErrorMessage(t, error))
    if (faqErrorCode(error) === 'permission_denied') onPermissionDenied?.()
  }

  const check = async () => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setMessage(t('faq.import.notXlsx'))
      return
    }
    if (file.size > MAX_FILE_BYTES) {
      setMessage(t('faq.import.tooLarge'))
      return
    }
    setBusy(true)
    setMessage('')
    try {
      setPreview(await previewFaqImport(tenantId, file))
      setOverwriteStale(false)
      setStep('preview')
    } catch (error) {
      fail(error)
    } finally {
      setBusy(false)
    }
  }

  // Back to the file step; its input starts empty, so no file is remembered.
  const chooseFile = () => {
    setFile(null)
    setStep('choose')
  }

  const commit = async () => {
    setBusy(true)
    setMessage('')
    try {
      const committed = await commitFaqImport(tenantId, preview.import_token, { overwriteStale })
      setResult(committed)
      setStep('done')
      onImported(committed)
    } catch (error) {
      fail(error)
      // The token is gone: only a new check can import this file.
      if (faqErrorCode(error) === 'import_token_expired') chooseFile()
    } finally {
      setBusy(false)
    }
  }

  const toApply = preview ? preview.created + preview.updated + (overwriteStale ? preview.stale : 0) : 0
  const actionName = (action) => t(`faq.import.actions.${action ?? 'none'}`)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div
        ref={root} role="dialog" aria-modal="true" aria-label={t('faq.import.title')} tabIndex={-1}
        onKeyDown={onKeyDown}
        aria-busy={busy}
        className="flex max-h-full w-full max-w-2xl flex-col gap-3 overflow-y-auto rounded-lg bg-white p-4 outline-none"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{t('faq.import.title')}</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label={t('faq.panel.close')}>
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {step === 'choose' && (
          <>
            <p className="text-sm text-slate-600">{t('faq.import.intro')}</p>
            {showTemplate && (
              <FaqDownloadButton
                label={t('faq.import.template')}
                fetchFile={() => downloadFaqTemplate(tenantId)}
                fallbackName={() => 'faq-template.xlsx'}
                onPermissionDenied={onPermissionDenied}
                className="self-start text-sm font-semibold text-[#1535c8] underline underline-offset-2 disabled:opacity-50"
              />
            )}
            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
              {t('faq.import.fileLabel')}
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null)
                  setMessage('')
                }}
                className="text-sm text-slate-600"
              />
            </label>
          </>
        )}

        {step === 'preview' && preview && (
          <>
            <p className="text-sm text-slate-600">{t('faq.import.rowsTotal', counted(preview.rows_total))}</p>
            <ul className="flex flex-wrap gap-2 text-sm">
              {['created', 'updated', 'unchanged', 'invalid', 'stale'].map((name) => (
                <li key={name} className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                  {t(`faq.import.summary.${name}`, counted(preview[name]))}
                </li>
              ))}
            </ul>
            <h3 className="text-sm font-semibold text-slate-700">{t('faq.import.reportTitle')}</h3>
            {preview.report.length === 0 ? (
              <p className="text-sm text-slate-500">{t('faq.import.noReport')}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {preview.report.map((item, index) => <ReportItem key={index} item={item} />)}
              </ul>
            )}
            {preview.truncated && <p className="text-xs text-slate-500">{t('faq.import.truncated')}</p>}
            {preview.invalid > 0 && <p className="text-sm text-slate-600">{t('faq.import.invalidSkipped')}</p>}
            {preview.stale > 0 && (
              <div className="flex flex-col gap-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                <p className="text-slate-700">{t('faq.import.staleHelp')}</p>
                <label className="flex items-center gap-2 font-medium text-slate-800">
                  <input
                    type="checkbox" checked={overwriteStale}
                    onChange={(event) => setOverwriteStale(event.target.checked)}
                  />
                  {t('faq.import.overwriteStale', counted(preview.stale))}
                </label>
              </div>
            )}
            {toApply === 0 && <p className="text-sm text-slate-600">{t('faq.import.nothingToImport')}</p>}
          </>
        )}

        {step === 'done' && result && (
          <div className="flex flex-col gap-2 text-sm text-slate-700">
            <p>{t('faq.import.done', {
              created: number(result.created),
              // Overwritten stale rows stay "stale" in the report but were applied as updates.
              updated: number(result.updated + (overwriteStale ? result.stale : 0)),
            })}</p>
            {result.sync_queued > 0 && <p>{t('faq.import.syncQueued')}</p>}
            {result.changed_since_preview.length > 0 && (
              <>
                <p>{t('faq.import.changedSincePreview', counted(result.changed_since_preview.length))}</p>
                <ul className="list-disc pl-5">
                  {result.changed_since_preview.map((change) => (
                    <li key={change.row}>
                      {t('faq.import.changedRow', {
                        row: change.row, before: actionName(change.before), after: actionName(change.after),
                      })}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {message && <p role="alert" className="text-sm text-red-600">{message}</p>}

        <div className="flex flex-wrap justify-end gap-2">
          {step === 'choose' && (
            <button type="button" onClick={check} disabled={!file || busy} className={`${button} bg-[#1535c8] text-white`}>
              {busy ? t('faq.import.checking') : t('faq.import.check')}
            </button>
          )}
          {step === 'preview' && (
            <>
              <button
                type="button" onClick={() => { setMessage(''); chooseFile() }} disabled={busy}
                className={`${button} border border-slate-200 bg-white text-slate-700`}
              >
                {t('faq.import.chooseAnother')}
              </button>
              <button
                type="button" onClick={commit} disabled={busy || toApply === 0}
                className={`${button} bg-[#1535c8] text-white`}
              >
                {busy ? t('faq.import.committing') : t('faq.import.commit', counted(toApply))}
              </button>
            </>
          )}
          {step === 'done' && (
            <button type="button" onClick={onClose} className={`${button} bg-[#1535c8] text-white`}>
              {t('faq.import.finish')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
